import { ID, Permission, Query, Role } from "appwrite";
import { z } from "zod";
import {
	AppwriteClient,
	getAppwriteClient,
	getCurrentUser,
} from "$/modules/appwrite/lib/appwrite";
import { buckets } from "$/modules/appwrite/lib/appwrite-buckets";
import { collections } from "$/modules/appwrite/lib/appwrite-collections";
import type {
	InputQuestion,
	Project,
	Question,
	Questionnaire,
} from "$/modules/appwrite/types/collections";
import { getTranslations } from "$/modules/i18n/lib/i18n";
import { requireProjectRole } from "$/modules/projects/lib/project-access";
import {
	deleteSharedQuestion,
	syncProjectEditorAccess,
} from "$/modules/projects/lib/projects";
import { decodeTextEscapes } from "$/modules/shared/lib/text-normalization";
import { getAssetId } from "$/modules/storage/lib/getAssetId.helper";
import { validateQuestionMedia } from "$/modules/storage/lib/question-media";
import { deleteFile, uploadFile } from "$/modules/storage/lib/storage";

const getAllQuestionsSchema = z.object({
	questionnaireId: z.string(),
});

export const getAllQuestions = async (
	data: z.infer<typeof getAllQuestionsSchema>,
) => {
	const appwrite = getAppwriteClient();
	const parsedData = getAllQuestionsSchema.parse(data);
	return await appwrite.tablesDB.listRows<Question>(
		AppwriteClient.databaseId,
		collections.questions,
		[
			Query.equal("questionnaire", parsedData.questionnaireId),
			Query.select(["*", "questionnaire.$id"]),
		],
	);
};

const createQuestionSchema = z.object({
	questionnaireId: z.string(),
	question: z.string().trim().min(3).max(500),
	questionType: z.enum(["Single", "Multiple", "Text", "Info"]),
	options: z.array(z.string()),
	correctOption: z.array(z.string()),
	asset: z.file().optional(),
	assetPosition: z
		.enum(["beforeQuestion", "afterQuestion"])
		.default("afterQuestion"),
	explanation: z.string().trim().max(2000).optional(),
});

const normalizeAnswers = (values: string[]) =>
	values
		.map((value) => decodeTextEscapes(value).trim())
		.filter((value) => value.length > 0);

const hasDuplicateValues = (values: string[]) =>
	new Set(values.map((value) => value.toLowerCase())).size !== values.length;

const creatorPermissions = (userId: string) => [
	Permission.read(Role.user(userId)),
	Permission.update(Role.user(userId)),
	Permission.delete(Role.user(userId)),
];

const validateAnswerModel = (
	questionType: Question["questionType"],
	options: string[],
	correctOption: string[],
	t: (key: string) => string,
) => {
	if (questionType === "Single" || questionType === "Multiple") {
		if (options.length < 2) throw new Error(t("atLeastTwoOptions"));
		if (hasDuplicateValues(options)) throw new Error(t("duplicateOptions"));
		if (correctOption.length === 0) throw new Error(t("selectCorrectOption"));
		if (questionType === "Single" && correctOption.length !== 1) {
			throw new Error(t("singleExactlyOneCorrect"));
		}
		if (questionType === "Multiple" && hasDuplicateValues(correctOption)) {
			throw new Error(t("duplicateCorrectOptions"));
		}
		if (correctOption.some((value) => !options.includes(value))) {
			throw new Error(t("correctOptionMustMatch"));
		}
	}

	if (questionType === "Text" && correctOption.length !== 1) {
		throw new Error(t("textNeedsAnswer"));
	}
};

export const createQuestion = async (data: FormData) => {
	const options = normalizeAnswers(data.getAll("option") as string[]);
	const correctOption = normalizeAnswers(
		data.getAll("correctOption") as string[],
	);
	const formData = Object.fromEntries(data.entries());
	const appwrite = getAppwriteClient();
	const user = await getCurrentUser();

	const t = await getTranslations("project.question.message.create");
	const validationT = await getTranslations("project.question.validation");

	if (!user) throw new Error("Unauthenticated");

	const parsedData = createQuestionSchema.parse({
		...formData,
		question: decodeTextEscapes(String(formData.question ?? "")),
		explanation: decodeTextEscapes(String(formData.explanation ?? "")),
		options,
		correctOption,
	});
	validateAnswerModel(
		parsedData.questionType,
		parsedData.options,
		parsedData.correctOption,
		validationT,
	);
	validateQuestionMedia(parsedData.asset, validationT);
	const questionnaire = await appwrite.tablesDB.getRow<Questionnaire>(
		AppwriteClient.databaseId,
		collections.questionnaires,
		parsedData.questionnaireId,
		[Query.select(["*", "project.$id"])],
	);
	const projectId =
		typeof questionnaire.project === "string"
			? questionnaire.project
			: questionnaire.project?.$id;
	if (!projectId)
		throw new Error("Der Fragenkatalog ist keinem Projekt zugeordnet.");
	const project = await appwrite.tablesDB.getRow<Project>(
		AppwriteClient.databaseId,
		collections.projects,
		projectId,
	);
	await requireProjectRole(project);
	await syncProjectEditorAccess({ projectId: project.$id });

	const assetUrl = await uploadFile({
		asset: parsedData.asset,
		bucketId: buckets.questions,
	});

	await appwrite.tablesDB.createRow<InputQuestion>(
		AppwriteClient.databaseId,
		collections.questions,
		ID.unique(),
		{
			question: parsedData.question,
			questionnaire: parsedData.questionnaireId,
			questionType: parsedData.questionType,
			options: parsedData.options,
			correctOption: parsedData.correctOption,
			asset: assetUrl,
			assetPosition: parsedData.assetPosition,
			userId: user.$id,
			explanation: parsedData.explanation || null,
		},
		creatorPermissions(user.$id),
	);
	await syncProjectEditorAccess({ projectId: project.$id });

	return {
		success: true,
		message: t("success"),
	};
};

const editQuestionSchema = createQuestionSchema.extend({
	id: z.string(),
	asset: z.instanceof(File).optional(),
	fileId: z.string().optional(),
	removeAsset: z.string().optional(),
});

export const editQuestion = async (data: FormData) => {
	const options = normalizeAnswers(data.getAll("option") as string[]);
	const correctOption = normalizeAnswers(
		data.getAll("correctOption") as string[],
	);
	const formData = Object.fromEntries(data.entries());
	const appwrite = getAppwriteClient();

	const parsedData = editQuestionSchema.parse({
		...formData,
		question: decodeTextEscapes(String(formData.question ?? "")),
		explanation: decodeTextEscapes(String(formData.explanation ?? "")),
		options,
		correctOption,
	});

	const t = await getTranslations("project.question.message");
	const validationT = await getTranslations("project.question.validation");
	validateAnswerModel(
		parsedData.questionType,
		parsedData.options,
		parsedData.correctOption,
		validationT,
	);
	validateQuestionMedia(parsedData.asset, validationT);
	const questionnaire = await appwrite.tablesDB.getRow<Questionnaire>(
		AppwriteClient.databaseId,
		collections.questionnaires,
		parsedData.questionnaireId,
		[Query.select(["*", "project.$id"])],
	);
	const projectId =
		typeof questionnaire.project === "string"
			? questionnaire.project
			: questionnaire.project?.$id;
	if (!projectId) {
		throw new Error("Der Fragenkatalog ist keinem Projekt zugeordnet.");
	}
	const project = await appwrite.tablesDB.getRow<Project>(
		AppwriteClient.databaseId,
		collections.projects,
		projectId,
	);
	await requireProjectRole(project);
	await syncProjectEditorAccess({ projectId: project.$id });

	const shouldRemoveAsset = parsedData.removeAsset === "true";
	if (shouldRemoveAsset && parsedData.fileId) {
		await deleteFile({ bucket: buckets.questions, assetId: parsedData.fileId });
	}
	const assetId = shouldRemoveAsset
		? null
		: await getAssetId("questions", parsedData.fileId, parsedData.asset);

	await appwrite.tablesDB.updateRow<Question>(
		AppwriteClient.databaseId,
		collections.questions,
		parsedData.id,
		{
			question: parsedData.question,
			questionType: parsedData.questionType,
			options: parsedData.options,
			correctOption: parsedData.correctOption,
			asset: assetId,
			assetPosition: parsedData.assetPosition,
			explanation: parsedData.explanation || null,
		},
	);

	await syncProjectEditorAccess({ projectId: project.$id });

	return {
		message: t("edit.success"),
		success: true,
	};
};

const deleteQuestionSchema = editQuestionSchema.pick({ id: true });

export const deleteQuestion = async (
	data: z.infer<typeof deleteQuestionSchema>,
) => {
	const t = await getTranslations("project.question.message.delete");
	const parsedData = deleteQuestionSchema.parse(data);
	await deleteSharedQuestion({ questionId: parsedData.id });
	return {
		success: true,
		message: t("success"),
	};
};
